import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiAlert, mdiChevronDown, mdiChevronUp, mdiLoading, mdiMenuDown, mdiMenuUp, mdiPause, mdiPlay } from '@mdi/js';
import { useAppliance } from '@/lib/useAppliance';
import { extractApiError } from '@/lib/apiError';
import type { ConexoesLinkDTO, ConexoesRespostaDTO } from '@webip/shared/src/dto/conexoes';
import { corDoLink, formatarTaxa, LIMIAR_LINK_CHEIO, ordenar, useConexoes } from './comum';
import styles from './conexoes.module.css';
import Grid, { GridItem } from '@/components/layout/Grid';

/** Tráfego somado de um grupo de conexões, separado por link (para a barra empilhada). */
type Agregado = {
    chave: string;
    nome: string;
    nomeSecundario?: string;
    download: number;
    upload: number;
    conexoes: number;
    porLink: Map<number | null, { download: number; upload: number }>;
};

function agrupar(
    conexoes: ConexoesRespostaDTO['conexoes'],
    chave: (c: ConexoesRespostaDTO['conexoes'][number]) => { chave: string; nome: string; nomeSecundario?: string },
): Agregado[] {
    const grupos = new Map<string, Agregado>();
    for (const c of conexoes) {
        const k = chave(c);
        let g = grupos.get(k.chave);
        if (!g) {
            g = { ...k, download: 0, upload: 0, conexoes: 0, porLink: new Map() };
            grupos.set(k.chave, g);
        }
        g.download += c.download_bps;
        g.upload += c.upload_bps;
        g.conexoes += 1;
        const l = g.porLink.get(c.wan_id) ?? { download: 0, upload: 0 };
        l.download += c.download_bps;
        l.upload += c.upload_bps;
        g.porLink.set(c.wan_id, l);
        // Nome que aparecer em qualquer conexão do grupo vale para ele.
        if (!g.nomeSecundario && k.nomeSecundario) g.nomeSecundario = k.nomeSecundario;
    }
    return [...grupos.values()];
}

const pct = (v: number) => `${(v * 100).toFixed(v < 0.1 ? 1 : 0)}%`;

/**
 * Barra dupla (download em cima, upload embaixo). Cada sentido é empilhado
 * por link, com `escala` = valor que preenche a barra inteira.
 */
function Barras({ g, links, escalaDown, escalaUp }: {
    g: Agregado;
    links: ConexoesLinkDTO[];
    escalaDown: number;
    escalaUp: number;
}) {
    const sentido = (campo: 'download' | 'upload', escala: number) => {
        const segs = [...g.porLink.entries()]
            .filter(([, v]) => v[campo] > 0)
            .map(([wanId, v]) => ({ wanId, frac: escala > 0 ? v[campo] / escala : 0 }));
        return (
            <div className={styles.barra}>
                {segs.map((s) => (
                    <div
                        key={String(s.wanId)}
                        className={styles.barraSeg}
                        style={{ width: `${Math.min(100, s.frac * 100)}%`, background: corDoLink(links, s.wanId) }}
                    />
                ))}
            </div>
        );
    };
    const titulo = `Download: ${escalaDown > 0 ? pct(g.download / escalaDown) : '0%'} · Upload: ${escalaUp > 0 ? pct(g.upload / escalaUp) : '0%'}`;
    return (
        <div className={styles.barras} title={titulo}>
            {sentido('download', escalaDown)}
            {sentido('upload', escalaUp)}
        </div>
    );
}

type ColunaAgregado = 'nome' | 'download' | 'upload' | 'conexoes';

function TabelaAgregado({ titulo, rotuloNome, dica, grupos, links, escalaDown, escalaUp, aoClicar }: {
    titulo: string;
    rotuloNome: string;
    dica: string;
    grupos: Agregado[];
    links: ConexoesLinkDTO[];
    escalaDown: number;
    escalaUp: number;
    aoClicar: (g: Agregado) => void;
}) {
    const [aberta, setAberta] = useState(true);
    const [ordem, setOrdem] = useState<{ coluna: ColunaAgregado; desc: boolean }>({ coluna: 'download', desc: true });
    const linhas = useMemo(
        () => ordenar(grupos, (g) => (ordem.coluna === 'nome' ? g.nome : g[ordem.coluna]), ordem.desc),
        [grupos, ordem],
    );

    const th = (coluna: ColunaAgregado, rotulo: string, num = false) => (
        <th
            className={`${styles.ordenavel} ${num ? styles.num : ''}`}
            onClick={() => setOrdem((o) => ({ coluna, desc: o.coluna === coluna ? !o.desc : num }))}
        >
            {rotulo}
            {ordem.coluna === coluna && <Icon path={ordem.desc ? mdiMenuDown : mdiMenuUp} size={0.7} style={{ verticalAlign: -4 }} />}
        </th>
    );

    return (
        <section className={styles.secao}>
            <button type="button" className={styles.secaoTitulo} onClick={() => setAberta((v) => !v)} aria-expanded={aberta}>
                {titulo}
                <Icon path={aberta ? mdiChevronUp : mdiChevronDown} size={1} />
            </button>
            {aberta && (
                <>
                    <p className={styles.secaoDica}>{dica}</p>
                    <div className={styles.tabelaWrap}>
                        <table className={styles.tabela}>
                            <thead>
                                <tr>
                                    {th('nome', rotuloNome)}
                                    {th('download', 'Download', true)}
                                    {th('upload', 'Upload', true)}
                                    {th('conexoes', 'Conexões', true)}
                                    <th title="Parcela do tráfego total desta tabela, por link">% Download<br />% Upload</th>
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((g) => (
                                    <tr key={g.chave} className={styles.linhaClicavel} onClick={() => aoClicar(g)}>
                                        <td>
                                            {g.nome}
                                            {g.nomeSecundario && <span className={styles.nomeSecundario}>({g.nomeSecundario})</span>}
                                        </td>
                                        <td className={styles.num}>{formatarTaxa(g.download)}</td>
                                        <td className={styles.num}>{formatarTaxa(g.upload)}</td>
                                        <td className={`${styles.num} ${styles.muted}`}>{g.conexoes}</td>
                                        <td><Barras g={g} links={links} escalaDown={escalaDown} escalaUp={escalaUp} /></td>
                                    </tr>
                                ))}
                                {linhas.length === 0 && (
                                    <tr><td colSpan={5} className={styles.vazio}>Sem tráfego no momento.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </section>
    );
}

/** Tabela dos links: a barra mostra o uso da velocidade contratada, quando informada. */
function TabelaLinks({ dados }: { dados: ConexoesRespostaDTO }) {
    const [aberta, setAberta] = useState(true);
    const navigate = useNavigate();

    const linhas = useMemo(() => {
        const porWan = agrupar(
            dados.conexoes.filter((c) => c.wan_id != null),
            (c) => ({ chave: String(c.wan_id), nome: '' }),
        );
        return dados.links.map((l) => {
            const g = porWan.find((x) => x.chave === String(l.wan_id));
            const download = g?.download ?? 0;
            const upload = g?.upload ?? 0;
            // A barra é sempre o uso do link: sem velocidade informada não há
            // barra (a parcela do total, na mesma coluna, pareceria uso).
            const capDown = l.download_kbps ? l.download_kbps * 1000 : null;
            const capUp = l.upload_kbps ? l.upload_kbps * 1000 : null;
            return {
                link: l,
                download,
                upload,
                conexoes: g?.conexoes ?? 0,
                fracDown: capDown ? download / capDown : null,
                fracUp: capUp ? upload / capUp : null,
                capDown,
                capUp,
            };
        });
    }, [dados]);

    const celula = (valor: number, frac: number | null, cap: number | null) => {
        const cheio = frac != null && frac >= LIMIAR_LINK_CHEIO;
        return (
            <td
                className={`${styles.num} ${cheio ? styles.cheio : ''}`}
                title={cap != null && frac != null ? `${pct(frac)} de ${formatarTaxa(cap)}` : undefined}
            >
                {formatarTaxa(valor)}
                {cheio && <Icon path={mdiAlert} size={0.65} className={styles.cheioIcone} aria-label="Perto do limite do link" />}
                {cap != null && frac != null && <span className={styles.capacidade}>{pct(frac)} de {formatarTaxa(cap)}</span>}
            </td>
        );
    };

    return (
        <section className={styles.secao}>
            <button type="button" className={styles.secaoTitulo} onClick={() => setAberta((v) => !v)} aria-expanded={aberta}>
                Tráfego dos links
                <Icon path={aberta ? mdiChevronUp : mdiChevronDown} size={1} />
            </button>
            {aberta && (
                <>
                    <p className={styles.secaoDica}>
                        Com a velocidade informada no Controle de banda, a barra mostra o uso do link e destaca
                        a partir de {Math.round(LIMIAR_LINK_CHEIO * 100)}%.
                    </p>
                    <div className={styles.tabelaWrap}>
                        <table className={styles.tabela}>
                            <thead>
                                <tr>
                                    <th>Link</th>
                                    <th className={styles.num}>Download</th>
                                    <th className={styles.num}>Upload</th>
                                    <th className={styles.num}>Conexões</th>
                                    <th>% Download<br />% Upload</th>
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((r) => {
                                    const cor = corDoLink(dados.links, r.link.wan_id);
                                    return (
                                        <tr key={r.link.wan_id}>
                                            <td className={styles.linkCor} style={{ borderLeftColor: cor }}>
                                                {r.link.nome}
                                                {r.link.uso === 'backup' && <span className={styles.nomeSecundario}>(backup)</span>}
                                            </td>
                                            {celula(r.download, r.fracDown, r.capDown)}
                                            {celula(r.upload, r.fracUp, r.capUp)}
                                            <td
                                                className={`${styles.num} ${styles.muted} ${styles.linhaClicavel}`}
                                                title="Ver as conexões"
                                                onClick={() => navigate('/conexoes')}
                                            >
                                                {r.conexoes}
                                            </td>
                                            <td>
                                                {r.fracDown == null && r.fracUp == null ? (
                                                    <span className={styles.semVelocidade}>
                                                        Sem velocidade no{' '}
                                                        <a href="#" onClick={(e) => { e.preventDefault(); navigate('/controle-banda'); }}>
                                                            Controle de banda
                                                        </a>
                                                    </span>
                                                ) : (
                                                    <div className={styles.barras}>
                                                        {[r.fracDown, r.fracUp].map((f, i) => (
                                                            f == null
                                                                ? <div key={i} className={styles.barraVazia} />
                                                                : (
                                                                    <div key={i} className={styles.barra}>
                                                                        <div
                                                                            className={styles.barraSeg}
                                                                            style={{ width: `${Math.min(100, f * 100)}%`, background: cor }}
                                                                        />
                                                                    </div>
                                                                )
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {linhas.length === 0 && (
                                    <tr><td colSpan={5} className={styles.vazio}>Nenhum link WAN cadastrado.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </section>
    );
}

export default function TrafegoLinksPage() {
    const { applianceId, selectedAppliance } = useAppliance();
    const navigate = useNavigate();
    const [executando, setExecutando] = useState(true);
    const query = useConexoes(applianceId, executando);
    const dados = query.data;

    const { dispositivos, entrada } = useMemo(() => {
        const conexoes = dados?.conexoes ?? [];
        return {
            dispositivos: agrupar(
                conexoes.filter((c) => c.direcao === 'saida'),
                (c) => ({ chave: c.origem_ip, nome: c.dispositivo_nome ?? c.origem_ip, nomeSecundario: c.dispositivo_nome ? c.origem_ip : undefined }),
            ),
            entrada: agrupar(
                conexoes.filter((c) => c.direcao === 'entrada' && c.redirecionada),
                (c) => ({ chave: c.origem_ip, nome: c.origem_ip, nomeSecundario: c.redirecionamento_nome ?? c.interno_ip }),
            ),
        };
    }, [dados]);

    const soma = (gs: Agregado[], campo: 'download' | 'upload') => gs.reduce((s, g) => s + g[campo], 0);
    const verConexoes = (g: Agregado) => navigate(`/conexoes?origem=${encodeURIComponent(g.chave)}`);
    const erro = query.error ? extractApiError(query.error, 'Não foi possível consultar o tráfego').message : null;

    return (
        <LayoutUsuario>
            <VBox className={styles.root} style={{ gap: '16px', padding: '16px' }}>
                <PageHeader
                    title={`Tráfego por link${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Quem está usando a Internet agora e por qual link — soma das taxas das conexões ativas."
                    actions={applianceId ? (
                        <Botao
                            variant="outline-secondary"
                            icon={executando ? mdiPause : mdiPlay}
                            onClick={() => setExecutando((v) => !v)}
                        >
                            {executando ? 'Executando' : 'Pausado'}
                        </Botao>
                    ) : undefined}
                />

                {!applianceId && (
                    <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
                        Selecione um appliance no menu lateral.
                    </p>
                )}

                {erro && <AlertBox variant="danger">{erro}</AlertBox>}

                {applianceId && query.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                        <Icon path={mdiLoading} size={1.5} spin />
                    </HBox>
                )}

                {dados && (
                    <>
                        <HBox style={{ alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <div className={styles.legenda} aria-label="Legenda de cores dos links">
                                {dados.links.map((l) => (
                                    <span key={l.wan_id} className={styles.linkChip}>
                                        <span className={styles.linkSwatch} style={{ background: corDoLink(dados.links, l.wan_id) }} />
                                        {l.nome}{l.uso === 'backup' ? ' (backup)' : ''}
                                    </span>
                                ))}
                            </div>
                            {query.isFetching && <Icon path={mdiLoading} size={0.8} spin className={styles.muted} />}
                            <span className={styles.coletado}>
                                Atualizado às {new Date(dados.coletado_em).toLocaleTimeString('pt-BR')}
                            </span>
                        </HBox>

                        <Grid>
                            <GridItem sm={12}>
                                <TabelaLinks dados={dados} />
                            </GridItem>
                            <GridItem sm={12} md={6}>
                                <TabelaAgregado
                                    titulo="Saída (dispositivos)"
                                    rotuloNome="Dispositivo"
                                    dica="Clique num dispositivo para ver as conexões dele."
                                    grupos={dispositivos}
                                    links={dados.links}
                                    escalaDown={soma(dispositivos, 'download')}
                                    escalaUp={soma(dispositivos, 'upload')}
                                    aoClicar={verConexoes}
                                />
                            </GridItem>
                            <GridItem sm={12} md={6}>
                                <TabelaAgregado
                                    titulo="Entrada (redirecionamentos)"
                                    rotuloNome="IP remoto"
                                    dica="Acessos vindos da Internet aos serviços publicados. Clique num IP para ver as conexões dele."
                                    grupos={entrada}
                                    links={dados.links}
                                    escalaDown={soma(entrada, 'download')}
                                    escalaUp={soma(entrada, 'upload')}
                                    aoClicar={verConexoes}
                                />
                            </GridItem>
                        </Grid>
                    </>
                )}
            </VBox>
        </LayoutUsuario>
    );
}
